import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Typography,
  TablePagination,
  TextField,
  useTheme,
  useMediaQuery,
  CardContent,
  Stack,
  InputAdornment,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Straighten as StraightenIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { catalogosService } from '../../services/catalogosService';
import { UnidadMedida } from '../../types';

const UnidadesMedida: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [items, setItems] = useState<UnidadMedida[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchData = async (pageNumber: number, pageSize: number, search?: string) => {
    try {
      setLoading(true);
      const res = await catalogosService.getUnidadesMedida(
        pageNumber,
        pageSize,
        search || undefined
      );
      setItems(res.items || []);
      setTotalCount(res.totalCount ?? 0);
    } catch (error) {
      console.error('Error al cargar unidades de medida:', error);
      enqueueSnackbar('Error al cargar unidades de medida', { variant: 'error' });
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      fetchData(page + 1, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(t);
  }, [page, rowsPerPage, searchTerm]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('¿Eliminar esta unidad de medida?')) return;
    try {
      await catalogosService.deleteUnidadMedida(id);
      enqueueSnackbar('Unidad de medida eliminada', { variant: 'success' });
      fetchData(page + 1, rowsPerPage, searchTerm);
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        (typeof error?.response?.data === 'string' ? error.response.data : null) ||
        'Error al eliminar';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const goEdit = (id: string) => {
    navigate(`/inventario/unidades-medida/${encodeURIComponent(id)}`);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <StraightenIcon sx={{ color: '#1976d2', fontSize: 36 }} />
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Unidades de medida
              </Typography>
            </Stack>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/inventario/unidades-medida/nuevo')}
            >
              Nueva unidad
            </Button>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por código o descripción..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' },
              }}
            />
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : isMobile ? (
            <Stack spacing={2}>
              {items.map((u) => (
                <Card key={u.idUnidadMedida} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {u.idUnidadMedida} — {u.descripcion}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Decimales: {u.cantDecimales ?? '—'} · Balanza: {u.usaBalanza ?? '—'} · Tolerancia
                      pick: {u.porcToleranciaPick ?? '—'}
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <IconButton size="small" onClick={() => goEdit(u.idUnidadMedida)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(u.idUnidadMedida)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Decimales
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Usa balanza</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      % tol. pick
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((u) => (
                    <TableRow key={u.idUnidadMedida} hover>
                      <TableCell>{u.idUnidadMedida}</TableCell>
                      <TableCell>{u.descripcion}</TableCell>
                      <TableCell align="right">{u.cantDecimales ?? '—'}</TableCell>
                      <TableCell>{u.usaBalanza ?? '—'}</TableCell>
                      <TableCell align="right">{u.porcToleranciaPick ?? '—'}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => goEdit(u.idUnidadMedida)} color="primary">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(u.idUnidadMedida)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default UnidadesMedida;
